import { CommonModule } from '@angular/common';
import { Component, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiTextfield } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import { ProxeemaUniversignTransaction } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';

@Component({
  selector: 'dbw-proxeema-universign-transactions-page',
  imports: [CommonModule, OuiButtonModule, OuiTextfield, StateCardComponent],
  templateUrl: './proxeema-universign-transactions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './proxeema-universign-transactions.component.scss',
})
export class ProxeemaUniversignTransactionsPageComponent {
  readonly since = signal(this.toDateInput(this.daysAgo(30)));
  readonly until = signal(this.toDateInput(new Date()));
  readonly reason = signal('');
  readonly transactions = signal<ProxeemaUniversignTransaction[]>([]);
  readonly loading = signal(false);
  readonly cancellingTransactionId = signal<string | undefined>(undefined);
  readonly error = signal<string | undefined>(undefined);
  readonly canCancel = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureUniversignCancel));

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService,
  ) {}

  load(): void {
    const range = this.getRange();
    if (!range) return;
    this.loading.set(true);
    this.error.set(undefined);
    this.apiService.getProxeemaPendingUniversignTransactions(range.since, range.until)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: transactions => this.transactions.set(transactions),
        error: error => this.error.set(error?.error?.detail ?? 'Le chargement des transactions Universign a échoué.'),
      });
  }

  cancel(transaction: ProxeemaUniversignTransaction): void {
    const reason = this.reason().trim();
    if (!reason || reason.length > 500) {
      this.snackbarService.open('Saisissez un motif de 500 caractères maximum avant l’annulation.', 'error');
      return;
    }
    if (!window.confirm(`Annuler définitivement la transaction Universign ${transaction.transactionId} ?`)) return;

    this.cancellingTransactionId.set(transaction.transactionId);
    this.apiService.cancelProxeemaUniversignTransaction(transaction.transactionId, { reason })
      .pipe(finalize(() => this.cancellingTransactionId.set(undefined)))
      .subscribe({
        next: () => {
          this.reason.set('');
          this.snackbarService.open('Transaction Universign annulée.', 'success');
          this.load();
        },
        error: error => this.snackbarService.open(error?.error?.detail ?? 'L’annulation de la transaction Universign a échoué.', 'error'),
      });
  }

  onSinceChange(value: string): void { this.since.set(value); }
  onUntilChange(value: string): void { this.until.set(value); }
  onReasonChange(value: string): void { this.reason.set(value); }

  private getRange(): { since: string; until: string } | undefined {
    const since = new Date(`${this.since()}T00:00:00`);
    const until = new Date(`${this.until()}T23:59:59.999`);
    if (Number.isNaN(since.valueOf()) || Number.isNaN(until.valueOf()) || until <= since || until.valueOf() - since.valueOf() > 180 * 86400000) {
      this.error.set('Sélectionnez une période valide de 180 jours maximum.');
      return undefined;
    }
    return { since: since.toISOString(), until: until.toISOString() };
  }

  private daysAgo(days: number): Date { const date = new Date(); date.setDate(date.getDate() - days); return date; }
  private toDateInput(date: Date): string { return date.toISOString().slice(0, 10); }
}
